<template>
  <v-sheet
    data-qa="attached_files"
    class="attached-files d-flex flex-column gr-4"
  >
    <div class="text-subtitle-1 text--text">
      Обязательно загрузите направление
    </div>
    <div
      v-if="attachedFilesStore.attachedFiles.length"
      data-qa="attached_files_previews"
      class="d-flex flex-wrap ga-4"
    >
      <FilePreview
        v-for="attachedFile in attachedFilesStore.attachedFiles"
        :key="attachedFile.id"
        :file="attachedFile.file"
        :status="attachedFile.status"
        :size="80"
        :hide-on-leave="isExceededAttachedFiles"
        @delete="attachedFilesStore.deleteAttachedFile(attachedFile)"
        @retry="retrySend(attachedFile)"
      />
    </div>
    <v-expand-transition>
      <CoreFilePicker
        v-show="!isExceededAttachedFiles"
        ref="coreFilePickerRef"
        :accept-file-types="VALIDATION_OPTIONS.allowedFileMimeTypes"
        :disabled="isExceededAttachedFiles"
        :file-validation-rules="fileValidationRules"
        :image-validation-rules="imageValidationRules"
        is-convert-to-base64
        @validation-error="alertDialogRef.open($event)"
        @pick-file-error="alertDialogRef.open(pickFileError)"
        @input="handleInputFilePicker"
      >
        <template #default="{ focused, disabled }">
          <FilePickerActivator
            :text="fileActivatorText"
            :focused="focused"
            :disabled="disabled"
            :is-error="attachedFilesStore.isErrorState"
          />
        </template>
      </CoreFilePicker>
    </v-expand-transition>
    <v-expand-transition>
      <BannerInfo v-if="isVisibleBannerOriginalDocuments">
        <div class="text-body-2 text--text">
          Не забудьте взять оригинал направления на приём
        </div>
      </BannerInfo>
    </v-expand-transition>
    <AlertDialog
      ref="alertDialogRef"
    />
  </v-sheet>
</template>

<script lang="ts" setup>
import {
  computed,
  ref,
} from 'vue'

import BannerInfo from 'www/vue_components/common/core/BannerInfo.vue'
import type {
  AttachedFile,
} from 'src/components/pages/appointment/uslugi/new/components/ServiceAppointmentForm/components/AttachedFiles/types'
import apiCheckDocument from 'src/components/pages/appointment/uslugi/new/components/ServiceAppointmentForm/components/AttachedFiles/api/apiCheckDocument'
import AlertDialog from 'src/components/pages/appointment/uslugi/new/components/ServiceAppointmentForm/components/AttachedFiles/components/AlertDialog.vue'
import CoreFilePicker from 'src/core/CoreFilePicker/CoreFilePicker.vue'
import FilePickerActivator from 'src/components/pages/appointment/uslugi/new/components/ServiceAppointmentForm/components/AttachedFiles/components/FilePickerActivator.vue'
import FilePreview from 'src/components/pages/appointment/uslugi/new/components/ServiceAppointmentForm/components/AttachedFiles/components/FilePreview/FilePreview.vue'
import useAttachedFilesStore from 'src/components/pages/appointment/uslugi/new/components/ServiceAppointmentForm/components/AttachedFiles/stores/useAttachedFilesStore'
import useValidationRules from 'src/components/pages/appointment/uslugi/new/components/ServiceAppointmentForm/components/AttachedFiles/composables/useValidationRules'
import {
  VALIDATION_OPTIONS,
} from 'src/components/pages/appointment/uslugi/new/components/ServiceAppointmentForm/components/AttachedFiles/const'
import checkIsImage from 'src/components/pages/appointment/uslugi/new/components/ServiceAppointmentForm/components/AttachedFiles/functions/checkIsImage'

const attachedFilesStore = useAttachedFilesStore()

const coreFilePickerRef = ref()
const alertDialogRef = ref()

const {
  fileValidationRules,
  imageValidationRules,
  pickFileError,
} = useValidationRules({
  openFilePicker: () => coreFilePickerRef.value.open(),
  validationOptions: VALIDATION_OPTIONS,
  attachedFileByteSizes: computed(() => attachedFilesStore.attachedFileByteSizes),
})

const isExceededAttachedFiles = computed(() => (attachedFilesStore.attachedFiles.length >= 2))
const fileActivatorText = computed(() => {
  const firstLine = VALIDATION_OPTIONS.allowedFileTypes.join(', ').toUpperCase()
  const secondLine = `не больше 2 файлов, каждый до ${VALIDATION_OPTIONS.maxSizeMb} МБ`

  return `${firstLine}\n${secondLine}`
})
const isVisibleBannerOriginalDocuments = computed(() => attachedFilesStore.attachedFiles.some(i => i.status === 'success'))

function handleInputFilePicker({ file }: { file: File }) {
  if (isExceededAttachedFiles.value) {
    return
  }

  const attachedFile = attachedFilesStore.createAttachedFile(file)

  sendAttachedFile(attachedFile)
}

function retrySend(attachedFile: AttachedFile) {
  if (attachedFile.status !== 'fail') {
    return
  }

  sendAttachedFile(attachedFile)
}

async function sendAttachedFile(attachedFile: AttachedFile) {
  // Сервис работает только с изображениями. PDF не нужно проверять, т.к. считается, что это уже документ
  if (!checkIsImage(attachedFile.file)) {
    attachedFile.status = 'success'
    return
  }

  attachedFile.status = 'loading'

  try {
    const data = await apiCheckDocument.post({ file: attachedFile.file }, ({ loaded, total }) => {
      if (attachedFile.isDeleted) {
        return
      }

      attachedFile.progress = Math.floor((loaded / total) * 100)
    })

    if (attachedFile.isDeleted) {
      return
    }

    if (!data.recognised_as_document) {
      alertDialogRef.value.open({
        title: 'Фото не подходит',
        text: 'Должен быть скан или фото направления',
        confirm: {
          text: 'Понятно',
          callback: () => {
            attachedFilesStore.deleteAttachedFile(attachedFile)
          },
        },
        persistent: true,
      })

      return
    }

    attachedFile.status = 'success'
  } catch (_error) {
    if (attachedFile.isDeleted) {
      return
    }

    attachedFile.status = 'fail'
  }
}
</script>

<style lang="scss" scoped>
.attached-files > :deep(.expand-transition-leave-active),
.attached-files > :deep(.expand-transition-enter) {
  margin-bottom: -16px; // компенсирует gr-4
}
</style>
